import logger from '../../log';
import ByteBuffer from 'bytebuffer';
import { NodeMessageTypes } from './NodeMessages';
import { chunk, MAX_DATA_SIZE_BYTES, unchunk } from './ArrayChunking';
import { Node, ConnectionState } from './interfaces';
import {
  DataEncoderDecoder,
  DataStartEncoderDecoder,
  EncoderMap,
  makeMessage,
  Message,
  MESSAGE_TYPES
} from './MessageEncodingDecoding';
import ObjectCache from './ObjectCache';
import PeerChannel, { ChannelError, RTCConstraints } from './PeerChannel';
import { RTCSignalOps } from './SignalBus';
import autobind from '../../autobind';
import { AgentCodes } from './AgentCodes';
import { CacheData } from './cache/interfaces';

const log = logger.getLogger(__filename);

enum State {
  PENDING,
  OPEN,
  CLOSED
}

const KEEP_ALIVE_INTERVAL = 3000;
const MAX_KEEP_ALIVES = 3;

export function handleMessage(
  data: ArrayBuffer,
  connection: PeerConnection
): void {
  if (data) {
    // Notify the SignalConnection that we are still live.
    // In the long run we may want to decouple this.
    // For now we need sockets open for new peers and it's good
    // to know we are still peering data.
    connection.signalConnection.tick();
    const bb = ByteBuffer.wrap(data);
    const type = bb.readByte();
    const msg = bb.toArrayBuffer();
    log.trace(
      `Received message of type: ${type} from peer ${connection.peer.nodeId}`
    );
    connection.lastPingReceived = Date.now();
    switch (type) {
      case MESSAGE_TYPES.KEEP_ALIVE: {
        log.trace(`Received keep-alive from ${connection.peer.nodeId}`);
        break;
      }
      case MESSAGE_TYPES.DATA: {
        const chunk = DataEncoderDecoder.decode(msg);
        connection.addChunks(chunk.objectId, chunk.index, chunk.data);
        break;
      }
      case MESSAGE_TYPES.DATA_START: {
        const dataStart = DataStartEncoderDecoder.decode(msg);
        if (dataStart.numChunks === 1) {
          connection.receivedFullPeerObjectResponse(
            dataStart.objectId,
            dataStart.firstChunk
          );
          return;
        }
        const handler = connection.getMessageHandler(
          MESSAGE_TYPES.NODE_MESSAGE
        );
        handler({
          type: NodeMessageTypes.PEER_OBJECT_RESPONSE,
          body: {
            from: connection.id,
            to: connection.node.nodeId,
            objectId: dataStart.objectId,
            status: dataStart.status,
            complete: false
          }
        });
        if (
          dataStart.numChunks === 0 ||
          dataStart.status !== AgentCodes.STATUS_OK ||
          !dataStart.firstChunk
        ) {
          // Nothing more to do, nothing is being transferred
          return;
        } else {
          const { objectId, firstChunk, numChunks } = dataStart;
          connection.addChunks(objectId, 0, firstChunk, numChunks);
        }
        return;
      }

      default: {
        log.trace('Received message from ' + connection.peer.nodeId);
        const encoder = EncoderMap.get(type);
        if (encoder) {
          const decoded = encoder.decode(msg);
          log.trace('Decoded message from peer', decoded);
          const handler = connection.getMessageHandler(type);
          handler(decoded);
        }
      }
    }
  }
}

interface PendingChunks {
  numChunks?: number;
  chunks: Map<number, ArrayBuffer>;
}

type MessageHandler = (args: Message) => void;

const noop: MessageHandler = () => undefined;

export default class PeerConnection {
  signalConnection: RTCSignalOps;
  cache: ObjectCache;
  peerChannel: PeerChannel;
  node: Node;
  peer: Node;
  pendingChunks: Map<string, PendingChunks>;
  private _messageHandlers: Map<number, MessageHandler>;
  pendingOutgoingMessages: never[];
  maxMessageSize: number;
  id: string;

  lastPingSent: number;
  lastPingReceived: number;

  onopen: () => void;
  onclose: (reason: number) => void;

  private state: State;
  private channelOpts: {
    rtcConfiguration: RTCConfiguration;
    signalConnection: RTCSignalOps;
    constraints: RTCConstraints | undefined;
  };

  constructor(
    signalConnection: RTCSignalOps,
    cache: ObjectCache,
    node: Node,
    peer: Node,
    rtcConfiguration: RTCConfiguration,
    constraints: RTCConstraints | undefined,
    onopen: () => void,
    onclose: (reason: number) => void,
    offer?: RTCSessionDescriptionInit
  ) {
    autobind(this);
    // id for this is the peers id
    this.id = peer.nodeId;
    this.signalConnection = signalConnection;
    this.cache = cache;
    this.channelOpts = {
      rtcConfiguration,
      signalConnection,
      constraints
    };

    //handlers
    this._messageHandlers = new Map();

    this.node = node;
    this.peer = peer;

    this.onopen = onopen;
    this.onclose = onclose;

    this.pendingChunks = new Map();
    this.pendingOutgoingMessages = [];
    this.maxMessageSize = MAX_DATA_SIZE_BYTES / 4;
    this.state = State.PENDING;

    this.lastPingSent = 0;
    this.lastPingReceived = 0;

    this.peerChannel = new PeerChannel({
      ...this.channelOpts,
      node: this.node,
      peer: this.peer,
      offer
    });
    this.peerChannel.listenToMessages().subscribe({
      next: msg => {
        handleMessage(msg, this);
      },
      error: err => {
        log.error('Failed while listening to messages', err);
      },
      complete: () => {
        log.debug(
          `Channel with ${this.peer.nodeId} stopped publishing messages`
        );
      }
    });

    this.peerChannel.listenToEvents().subscribe({
      next: event => {
        //
        const { state, error } = event;
        // if we aren't yet open, then signal open
        if (
          state === ConnectionState.ESTABLISHED &&
          this.state === State.PENDING
        ) {
          this.state = State.OPEN;
          this.onopen();
          this.lastPingSent = Date.now();
          this._sendKeepAlive();
        }

        if (
          (state === ConnectionState.CLOSED ||
            state === ConnectionState.FAILED) &&
          (this.state === State.OPEN || this.state === State.PENDING)
        ) {
          const status = error
            ? error.statusCode
            : AgentCodes.CONNECTION_CLOSED;
          this.state = State.CLOSED;
          this.onclose(status);
        }
      },
      error: err => {
        log.error('Failed while listening to events', err);
      },
      complete: () => {
        log.debug(`Channel with ${this.peer.nodeId} stopped publishing events`);
      }
    });
  }

  isClosed(): boolean {
    return this.peerChannel.isClosed();
  }

  close(err?: ChannelError): void {
    const connectionState = ConnectionState;
    this.peerChannel.close(connectionState.CLOSED, err);
  }

  setMessageHandler(type: number, handler: MessageHandler): void {
    this._messageHandlers.set(type, handler);
  }

  // Get a message handler for the event type, if there is none return a noop function
  getMessageHandler(type: number): MessageHandler {
    const handler = this._messageHandlers.get(type);
    if (!handler) {
      log.debug('No handler found for', type);
    }
    return handler ?? noop;
  }

  _sendKeepAlive(): void {
    if (!this.peerChannel || this.isClosed()) {
      return;
    }
    if (
      this.lastPingReceived > 0 &&
      Date.now() - this.lastPingReceived > KEEP_ALIVE_INTERVAL * MAX_KEEP_ALIVES
    ) {
      log.debug(
        `Too long since last keep-alive: lost connection with peer ${this.peer.nodeId}`
      );
      this.close(
        new ChannelError(
          'Channel timed out on keep-alive',
          AgentCodes.CONNECTION_KEEP_ALIVE_TIMEOUT
        )
      );
      return;
    }
    const now = Date.now();
    const nextKeepAliveTs = this.lastPingSent + KEEP_ALIVE_INTERVAL;
    setTimeout(
      () => {
        try {
          const msg = makeMessage(MESSAGE_TYPES.KEEP_ALIVE);
          log.trace('Sending keep-alive to peer');
          this.lastPingSent = Date.now();
          this.peerChannel.send(msg);
        } catch (e) {
          log.warn('Failed to send keep-alive');
        }
        this._sendKeepAlive();
      },
      nextKeepAliveTs > now ? nextKeepAliveTs - now : KEEP_ALIVE_INTERVAL
    );
  }

  _encodeMessage(messageType: number, msg: Message): ArrayBuffer | null {
    if (EncoderMap.has(messageType) && msg != null) {
      return EncoderMap.get(messageType).encode(msg);
    }
    log.info('sendMessage was sent an unknown item', {
      encoder: EncoderMap.get(messageType),
      messageType,
      msg
    });
    return null;
  }

  sendMessage(messageType: number, message: Message): number {
    try {
      log.trace('Encoding message type ' + messageType, message);
      const data = this._encodeMessage(messageType, message);
      const msg = makeMessage(messageType, data);
      log.trace(
        'Sending message type: ' + messageType,
        ' to peer ',
        this.peer,
        ' with message: ',
        msg
      );
      const result = this.peerChannel.send(msg);
      if (result === AgentCodes.STATUS_OK) {
        this.lastPingSent = Date.now();
      }
      return result;
    } catch (e) {
      log.warn(`Failed to send message type: ${messageType}`, message, e);
      return AgentCodes.CONNECTION_SEND_FAILURE;
    }
  }

  setRemoteAnswer(answer: RTCSessionDescriptionInit): void {
    this.peerChannel.setRemoteAnswer(answer);
  }

  addRemoteCandidate(candidate: RTCIceCandidateInit): void {
    this.peerChannel.addRemoteCandidate(candidate);
  }

  restartConnection(offer: RTCSessionDescriptionInit): void {
    this.peerChannel.handleOffer(offer, true);
  }

  async receivedFullPeerObjectResponse(
    objectId: string,
    payload?: ArrayBuffer
  ): Promise<void> {
    const handler = this.getMessageHandler(MESSAGE_TYPES.NODE_MESSAGE);
    const type = NodeMessageTypes.PEER_OBJECT_RESPONSE;
    const { id: from } = this;
    // Regardless of the cache save result, this response is complete
    const body = { from, to: this.node.nodeId, objectId, complete: true };
    if (payload && payload.byteLength > 0) {
      return this.cache
        .set(objectId, payload)
        .then(() => {
          const status = AgentCodes.STATUS_OK;
          handler({ type, body: { ...body, status } });
        })
        .catch(err => {
          log.warn('Unable to save object ' + objectId + ' to cache: ' + err);
          // Internal error: unable to save to cache, report it to the kernel
          const status = AgentCodes.CACHE_SAVE_FAILURE;
          handler({ type, body: { ...body, status } });
        })
        .finally(() => {
          if (this.pendingChunks.has(objectId)) {
            this.pendingChunks.delete(objectId);
          }
        });
    } else {
      // Internal error: invalid payload after dechunking
      const status = AgentCodes.INVALID_PAYLOAD;
      handler({ type, body: { ...body, status } });
      return Promise.resolve();
    }
  }

  getPendingChunk(objectId: string, numChunks?: number): PendingChunks {
    const entry = this.pendingChunks.get(objectId);
    if (!entry) {
      // Haven't gotten a chunk yet for this chunklist
      const item = { numChunks, chunks: new Map() };
      this.pendingChunks.set(objectId, item);
      return item;
    } else if (numChunks) {
      entry.numChunks = numChunks;
    }
    return entry;
  }

  addChunks(
    objectId: string,
    index: number,
    data: ArrayBuffer,
    numChunks?: number
  ): void {
    const entry = this.getPendingChunk(objectId, numChunks);
    entry.chunks.set(index, data);
    this.mergeChunksIfAble(entry, objectId);
  }

  private mergeChunksIfAble(entry: PendingChunks, objectId: string): void {
    const { numChunks, chunks } = entry;
    if (typeof numChunks !== 'number' || numChunks !== chunks.size) {
      return;
    }
    const arrayOfBuffers = [];
    for (let i = 0; i < numChunks; i++) {
      if (!chunks.has(i)) {
        return;
      }
      arrayOfBuffers.push(chunks.get(i));
    }
    const unchunked = unchunk(arrayOfBuffers);
    // unchunked is a byte array if segment has bytes and is complete.
    if (unchunked) {
      this.receivedFullPeerObjectResponse(objectId, unchunked);
    }
  }

  async sendPeerObjectResponse(
    objectId: string,
    status: number,
    metadata: string
  ): Promise<void> {
    if (status !== AgentCodes.STATUS_OK) {
      // Don't bother fetching from the cache if the kernel doesn't intend to send anything
      this.sendMessage(MESSAGE_TYPES.DATA_START, {
        from: this.node.nodeId,
        to: this.id,
        objectId,
        metadata,
        status,
        numChunks: 0
      });
      return Promise.resolve();
    }
    return this.cache
      .get(objectId)
      .then(cacheItem => {
        try {
          if (!cacheItem) {
            // Some how the cache get succeeded but returned an undefined object? Shouldn't happen but handle it anyways
            log.warn(
              'Cache get indicated success, but returned item is undefined'
            );
            status = AgentCodes.INVALID_CACHE_ENTRY;
          } else {
            status = this.sendCachedItem(cacheItem, objectId, metadata);
            if (status === AgentCodes.STATUS_OK) {
              return;
            }
            log.info(`No chunks generated by object for ${objectId}`);
          }
        } catch (error) {
          log.warn(`Could not send object response for ${objectId}`, error);
          status = AgentCodes.CONTENT_ENCODING_FAILURE;
        }
        // If we make it here, we failed at some point to send the content, attempt to report the error to the other
        // side to avoid a costly timeout
        this.sendMessage(MESSAGE_TYPES.DATA_START, {
          from: this.node.nodeId,
          to: this.id,
          objectId,
          status, // http: internal error
          numChunks: 0
        });
      })
      .catch(error => {
        // Kernel wants to send data that's not actually in the cache, shouldn't happen, but report it anyways
        log.warn(
          'Kernel requested to send data to peer that is not in the cache',
          error
        );
        this.sendMessage(MESSAGE_TYPES.DATA_START, {
          from: this.node.nodeId,
          to: this.id,
          objectId,
          status: AgentCodes.INVALID_CACHE_ENTRY, // http: Not found
          numChunks: 0
        });
      });
    // TODO: Errors generated by sending these messages need to reported back to the kernel at some point
  }

  private sendCachedItem(
    cacheItem: CacheData,
    objectId: string,
    metadata: string
  ): number {
    // Reserve some space for extra metadata beyond the chunk payload
    const chunkedPayload = chunk(cacheItem, this.maxMessageSize);
    const length = chunkedPayload.length;
    if (chunkedPayload.length < 1) {
      return AgentCodes.CONTENT_ENCODING_FAILURE;
    }
    // Send the first chunk along with the initial content response and status code
    this.sendMessage(MESSAGE_TYPES.DATA_START, {
      from: this.node.nodeId,
      to: this.id,
      objectId,
      metadata,
      status: AgentCodes.STATUS_OK,
      numChunks: length,
      firstChunk: chunkedPayload[0]
    });
    let status = AgentCodes.STATUS_OK;
    // Subsequent chunks are sent as data, to be reassembled by the other side
    for (let i = 1; i < length; i++) {
      status = this.sendMessage(MESSAGE_TYPES.DATA, {
        objectId,
        index: i,
        data: chunkedPayload[i]
      });
      if (status !== AgentCodes.STATUS_OK) {
        log.warn(
          `Failed to send chunked data for ${objectId} to ${this.id}`,
          status
        );
        break;
      }
    }
    // The last status, generally all succeeded.
    return status;
  }
}
